import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ListChecks, Loader2, RefreshCw, AlertTriangle, FileText, Sparkles,
  ChevronDown, ChevronRight, Search, X, Filter, Check, MessageSquareText,
  Quote, Layers, Plus, Minus, GitCompare, History, ShieldAlert, FileWarning, MapPin,
  ScanSearch, ShieldCheck, Download, FileSpreadsheet,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/common/Feedback';
import { DocumentViewerDialog } from '@/components/projects/ScopeDocuments';
import api from '@/lib/api';
import { toast } from 'sonner';

// The single unified taxonomy — ordered for display (matches backend REQUIREMENT_TYPES).
const TYPE_ORDER = [
  'Scope inclusion', 'Exclusion', 'Separate-pay item', 'Commercial/payment condition',
  'Contractor obligation', 'Employer obligation', 'Material/specification',
  'Quality/testing requirement', 'Submittal/documentation', 'Time/programme requirement',
  'Notice/procedural requirement', 'General condition',
];

const LOW_CONF = 0.6;   // below this = weakly grounded (warning)
const MED_CONF = 0.8;   // below this = medium

// Per-type colour family for the group header accent (only known theme colours used).
const TYPE_META = {
  'Scope inclusion': { dot: 'bg-brand-500' },
  'Exclusion': { dot: 'bg-danger-500' },
  'Separate-pay item': { dot: 'bg-money-500' },
  'Commercial/payment condition': { dot: 'bg-money-500' },
  'Contractor obligation': { dot: 'bg-slate-400' },
  'Employer obligation': { dot: 'bg-amber-500' },
  'Material/specification': { dot: 'bg-slate-400' },
  'Quality/testing requirement': { dot: 'bg-slate-400' },
  'Submittal/documentation': { dot: 'bg-slate-400' },
  'Time/programme requirement': { dot: 'bg-slate-400' },
  'Notice/procedural requirement': { dot: 'bg-amber-500' },
  'General condition': { dot: 'bg-slate-400' },
};
const typeMeta = (t) => TYPE_META[t] || { dot: 'bg-slate-400' };
const ALL_TYPES = TYPE_ORDER;
const typeRank = (t) => { const i = TYPE_ORDER.indexOf(t); return i === -1 ? 99 : i; };

const confBucket = (c) => {
  if (typeof c !== 'number') return 'unscored';
  if (c < LOW_CONF) return 'low';
  if (c < MED_CONF) return 'med';
  return 'high';
};
const confSortVal = (c) => (typeof c === 'number' ? c : 1.1); // unscored sorts last
const pageVal = (r) => (typeof r.page === 'number' ? r.page : Number.POSITIVE_INFINITY); // null pages last

export const RequirementsTab = ({ projectId, projectName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [gapsOpen, setGapsOpen] = useState(false);
  const [view, setView] = useState(null);
  const pollRef = useRef(null);

  // Filters + sort
  const [search, setSearch] = useState('');
  const [fType, setFType] = useState('all');
  const [fDoc, setFDoc] = useState('all');
  const [fParent, setFParent] = useState('all');
  const [fConf, setFConf] = useState('all');
  const [sortWeak, setSortWeak] = useState(false);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Manual expand/collapse sets
  const [openTypes, setOpenTypes] = useState(() => new Set());
  const [openParents, setOpenParents] = useState(() => new Set());

  // Proposal review
  const [proposalOpen, setProposalOpen] = useState(false);
  const [addSel, setAddSel] = useState(() => new Set());
  const [remSel, setRemSel] = useState(() => new Set());
  const [mergeSel, setMergeSel] = useState(() => new Set());
  const [applying, setApplying] = useState(false);

  const fetchReqs = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/requirements`);
      setData(r.data);
      return r.data;
    } catch { return { status: 'failed' }; }
  };

  const isBusy = (d) => d?.status === 'processing' || d?.proposal_status === 'processing' || d?.completeness_status === 'processing';

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await fetchReqs();
      if (!isBusy(d) && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 4000);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const d = await fetchReqs();
      if (!active) return;
      setLoading(false);
      if (isBusy(d)) startPolling();
    })();
    return () => { active = false; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/projects/${projectId}/requirements/refresh`);
      if (r.data?.proposal_status === 'processing') {
        toast.success('Checking for changes to your baseline…');
        setData({ ...data, proposal_status: 'processing' });
      } else {
        toast.success('Re-extracting requirements…');
        setData({ ...data, status: 'processing' });
      }
      startPolling();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not refresh'); }
    finally { setRefreshing(false); }
  };

  const runCompletenessCheck = async () => {
    setChecking(true);
    try {
      await api.post(`/projects/${projectId}/requirements/completeness-check`);
      toast.success('Running AI completeness check — recovering any missed requirements…');
      setData((d) => ({ ...d, completeness_status: 'processing' }));
      startPolling();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start completeness check'); }
    finally { setChecking(false); }
  };

  // ---- Export (CSV / PDF) -------------------------------------------------
  const exportRows = () => {
    // Export the currently-visible (filtered) requirements in a stable order.
    const rows = (filtered && filtered.length ? filtered : reqs);
    return [...rows].sort((a, b) => pageVal(a) - pageVal(b) || (a.type || '').localeCompare(b.type || ''));
  };
  const safeName = (projectName || 'Recoup').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'Recoup';
  const stamp = new Date().toISOString().slice(0, 10);

  const exportCSV = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error('No requirements to export'); return; }
    const cols = ['#', 'Requirement', 'Type', 'Clause / Section', 'Parent', 'Page',
                  'Mandatory', 'Confidence', 'Verified', 'Source quote', 'Document'];
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(',')];
    rows.forEach((r, i) => {
      lines.push([
        i + 1, r.requirement, r.type, r.section || '', r.parent || '',
        (typeof r.page === 'number' ? r.page : ''),
        r.mandatory ? 'Yes' : 'No',
        (typeof r.confidence === 'number' ? Math.round(r.confidence * 100) + '%' : ''),
        r.verified === false ? 'No' : 'Yes',
        r.source_quote || '',
        (r.sources && r.sources[0]?.document) || r.document || '',
      ].map(esc).join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${safeName}_requirements_${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} requirements to CSV`);
  };

  const exportPDF = async () => {
    const rows = exportRows();
    if (!rows.length) { toast.error('No requirements to export'); return; }
    try {
      const { default: jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(15); doc.setTextColor(20);
      doc.text(`${projectName || 'Recoup'} — Extracted Requirements`, 40, 40);
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`${rows.length} requirements · Exported ${new Date().toLocaleString()}`, 40, 56);
      autoTable(doc, {
        startY: 70,
        head: [['#', 'Requirement', 'Type', 'Clause', 'Pg', 'Conf', 'Source quote']],
        body: rows.map((r, i) => [
          i + 1, r.requirement || '', r.type || '', r.section || '',
          (typeof r.page === 'number' ? r.page : ''),
          (typeof r.confidence === 'number' ? Math.round(r.confidence * 100) + '%' : ''),
          r.source_quote || '',
        ]),
        styles: { fontSize: 7.5, cellPadding: 3, valign: 'top', overflow: 'linebreak' },
        headStyles: { fillColor: [37, 99, 79], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 248, 246] },
        columnStyles: {
          0: { cellWidth: 22 }, 1: { cellWidth: 260 }, 2: { cellWidth: 95 },
          3: { cellWidth: 60 }, 4: { cellWidth: 24 }, 5: { cellWidth: 34 }, 6: { cellWidth: 250 },
        },
        margin: { left: 40, right: 40 },
      });
      doc.save(`${safeName}_requirements_${stamp}.pdf`);
      toast.success(`Exported ${rows.length} requirements to PDF`);
    } catch (e) {
      toast.error('Could not generate PDF');
    }
  };

  const reqs = useMemo(() => data?.requirements || [], [data]);
  // "Weakest first" only makes sense when something is actually weak — hide it
  // (and disable its effect) when every requirement is at 100% confidence.
  const allFullConfidence = useMemo(() => reqs.length > 0 && reqs.every((r) => r.confidence === 1), [reqs]);
  useEffect(() => { if (allFullConfidence && sortWeak) setSortWeak(false); }, [allFullConfidence, sortWeak]);
  const proposal = data?.proposal || null;
  const proposalStatus = data?.proposal_status || 'none';
  const coverage = data?.coverage || null;
  const completenessStatus = data?.completeness_status || 'none';
  const audit = useMemo(() => data?.audit || [], [data]);
  const version = data?.version || 0;

  // Toast a short summary when a completeness check finishes.
  const prevCheckRef = useRef('none');
  useEffect(() => {
    if (prevCheckRef.current === 'processing' && completenessStatus === 'ready') {
      const n = coverage?.completeness_added ?? 0;
      if (n > 0) toast.success(`Completeness check done — recovered ${n} missed requirement${n !== 1 ? 's' : ''}`);
      else toast.success('Completeness check done — no missed requirements found');
    }
    prevCheckRef.current = completenessStatus;
  }, [completenessStatus, coverage]);
  const additions = useMemo(() => proposal?.additions || [], [proposal]);
  const removals = useMemo(() => proposal?.removals || [], [proposal]);
  const merges = useMemo(() => proposal?.merges || [], [proposal]);

  // When a fresh proposal arrives, default-select all additions & merges, no removals.
  useEffect(() => {
    if (proposalStatus === 'pending') {
      setAddSel(new Set(additions.map((a) => a.id)));
      setRemSel(new Set());
      setMergeSel(new Set(merges.map((m) => m.id)));
    }
  }, [proposalStatus, additions, merges]);

  // Filter option lists
  const docOptions = useMemo(() => {
    const s = new Set();
    reqs.forEach((r) => {
      (r.sources && r.sources.length ? r.sources : [{ document: r.document }])
        .forEach((src) => { if (src.document) s.add(src.document); });
    });
    return Array.from(s);
  }, [reqs]);
  const parentOptions = useMemo(() => Array.from(new Set(reqs.map((r) => r.parent).filter(Boolean))), [reqs]);
  const typeOptions = useMemo(() => ALL_TYPES.filter((t) => reqs.some((r) => r.type === t)), [reqs]);

  const filtering = search.trim() !== '' || fType !== 'all' || fDoc !== 'all' || fParent !== 'all' || fConf !== 'all' || unverifiedOnly;

  const matchesDoc = (r, doc) => {
    if (doc === 'all') return true;
    const srcs = r.sources && r.sources.length ? r.sources : [{ document: r.document }];
    return srcs.some((s) => s.document === doc);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = reqs.filter((r) => {
      if (q && !(`${r.requirement} ${r.section} ${r.parent} ${r.source_quote}`.toLowerCase().includes(q))) return false;
      if (fType !== 'all' && r.type !== fType) return false;
      if (!matchesDoc(r, fDoc)) return false;
      if (fParent !== 'all' && r.parent !== fParent) return false;
      if (fConf !== 'all' && confBucket(r.confidence) !== fConf) return false;
      if (unverifiedOnly && r.verified !== false) return false;
      return true;
    });
    if (sortWeak) out = [...out].sort((a, b) => confSortVal(a.confidence) - confSortVal(b.confidence));
    return out;
  }, [reqs, search, fType, fDoc, fParent, fConf, sortWeak, unverifiedOnly]);

  // Group filtered: type -> parent('' = loose) -> [reqs].
  // Within each type, requirements are ordered by PAGE NUMBER (nulls last), and
  // clause/BOQ sub-groups are ordered by their first page — unless "Weakest first"
  // is active, which intentionally keeps the confidence ordering.
  const grouped = useMemo(() => {
    const byType = {};
    filtered.forEach((r) => {
      const t = r.type || 'General condition';
      byType[t] = byType[t] || { total: 0, parents: {}, loose: [] };
      byType[t].total += 1;
      if (r.parent) { (byType[t].parents[r.parent] = byType[t].parents[r.parent] || []).push(r); }
      else byType[t].loose.push(r);
    });
    const byPage = (a, b) => (pageVal(a) - pageVal(b)) || (a.requirement || '').localeCompare(b.requirement || '');
    Object.values(byType).forEach((g) => {
      if (!sortWeak) {
        g.loose.sort(byPage);
        Object.values(g.parents).forEach((arr) => arr.sort(byPage));
      }
      const keys = Object.keys(g.parents);
      g.parentKeys = sortWeak
        ? keys.sort()
        : keys.sort((k1, k2) => {
            const p1 = Math.min(...g.parents[k1].map(pageVal));
            const p2 = Math.min(...g.parents[k2].map(pageVal));
            return (p1 - p2) || k1.localeCompare(k2);
          });
    });
    return byType;
  }, [filtered, sortWeak]);

  const types = useMemo(() => Object.keys(grouped).sort((a, b) => typeRank(a) - typeRank(b)), [grouped]);

  const toggleType = (t) => setOpenTypes((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const toggleParent = (k) => setOpenParents((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const typeOpen = (t) => openTypes.has(t);
  const parentOpen = (k) => openParents.has(k);

  // Auto-expand all matching groups WHEN the filter/sort changes so matches are
  // visible — but only on that change, so manual collapse keeps working afterward.
  useEffect(() => {
    if (!(filtering || sortWeak)) return;
    const tset = new Set(Object.keys(grouped));
    const pset = new Set();
    Object.entries(grouped).forEach(([t, g]) => Object.keys(g.parents).forEach((pk) => pset.add(`${t}|${pk}`)));
    setOpenTypes(tset);
    setOpenParents(pset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fType, fDoc, fParent, fConf, unverifiedOnly, sortWeak]);

  const clearFilters = () => { setSearch(''); setFType('all'); setFDoc('all'); setFParent('all'); setFConf('all'); setSortWeak(false); setUnverifiedOnly(false); };

  const confirmReq = async (id) => {
    try { await api.post(`/projects/${projectId}/requirements/${id}/confirm`); toast.success('Requirement confirmed'); fetchReqs(); }
    catch { toast.error('Could not confirm'); }
  };
  const dismissReq = async (id) => {
    try { await api.delete(`/projects/${projectId}/requirements/${id}`); toast('Requirement dismissed'); fetchReqs(); }
    catch { toast.error('Could not dismiss'); }
  };

  const applyProposal = async () => {
    setApplying(true);
    try {
      await api.post(`/projects/${projectId}/requirements/proposal/apply`, {
        addition_ids: Array.from(addSel), removal_ids: Array.from(remSel),
        merge_group_ids: Array.from(mergeSel),
      });
      toast.success(`Baseline updated · ${addSel.size} added, ${remSel.size} removed, ${mergeSel.size} merged`);
      setProposalOpen(false);
      fetchReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply changes'); }
    finally { setApplying(false); }
  };
  const discardProposal = async () => {
    setApplying(true);
    try {
      await api.post(`/projects/${projectId}/requirements/proposal/discard`);
      toast('Proposed changes discarded — baseline unchanged');
      setProposalOpen(false);
      fetchReqs();
    } catch { toast.error('Could not discard'); }
    finally { setApplying(false); }
  };

  const rollback = async (v) => {
    try {
      const r = await api.post(`/projects/${projectId}/requirements/rollback/${v}`);
      toast.success(`Restored version v${v} (now v${r.data?.version})`);
      setHistoryOpen(false);
      fetchReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore that version'); }
  };

  if (loading) return <Card className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading requirements…</Card>;

  const status = data?.status || 'idle';

  if (status === 'processing') {
    const pr = data?.progress || null;
    const total = pr?.total || 0;
    const done = Math.min(pr?.done || 0, total || Infinity);
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;
    const phaseLabel = {
      extracting: 'Reading & classifying requirements',
      dedup: 'Merging duplicate requirements',
      saving: 'Saving the baseline',
      completeness: 'Checking for missed requirements',
    }[pr?.phase] || 'Extracting & classifying requirements';
    return (
      <Card className="p-8 flex flex-col items-center justify-center text-center" data-testid="requirements-processing">
        <div className="relative mb-4"><Sparkles className="h-8 w-8 text-brand" /><Loader2 className="h-12 w-12 animate-spin text-brand/30 absolute -top-2 -left-2" /></div>
        <div className="text-sm font-semibold text-slate-700">{phaseLabel}…</div>
        {total > 0 ? (
          <div className="w-full max-w-md mt-4">
            <Progress value={pct} className="h-2" data-testid="requirements-progress-bar" />
            <div className="flex items-center justify-between mt-1.5 text-xs text-slate-500" data-testid="requirements-progress-text">
              <span>Section {done} of {total}</span>
              <span className="font-medium text-slate-600">{pct}%</span>
            </div>
            {pr?.doc && <p className="text-[11px] text-slate-400 mt-1 truncate max-w-md">Now reading: {pr.doc}</p>}
          </div>
        ) : (
          <p className="text-xs text-slate-500 mt-1">This reads every uploaded document in full. Large document sets can take several minutes.</p>
        )}
        <p className="text-[11px] text-slate-400 mt-3">You can leave this page — extraction continues in the background.</p>
      </Card>
    );
  }

  if (status === 'budget_exceeded' || status === 'failed') {
    return (
      <Card className="p-8" data-testid="requirements-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">{status === 'budget_exceeded' ? 'AI budget exceeded' : 'Requirements extraction failed'}</div>
            <p className="text-xs text-slate-500 mt-1">{data?.error || 'Something went wrong while reading the documents.'}</p>
            <Button size="sm" className="mt-3 bg-primary hover:bg-ink-700" onClick={refresh} disabled={refreshing} data-testid="requirements-retry">
              {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (status === 'idle' || reqs.length === 0) {
    return (
      <Card className="p-0">
        <EmptyState icon={ListChecks} title="No requirements extracted"
          description="Upload native-text PDF documents when creating the project and Recoup will pull out and classify the requirements automatically." />
        <div className="px-5 pb-5 -mt-2">
          <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing} data-testid="requirements-extract-now">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}Extract now
          </Button>
        </div>
      </Card>
    );
  }

  const docCount = docOptions.length;
  const lowCount = reqs.filter((r) => confBucket(r.confidence) === 'low').length;
  const unverifiedCount = reqs.filter((r) => r.verified === false).length;
  const coverageIncomplete = coverage && coverage.fully_covered === false;

  // Deterministic clause-coverage signal (which detected sections have no requirement).
  const sectionsSeen = coverage?.sections_seen ?? 0;
  const sectionsWithReqs = coverage?.sections_with_reqs ?? 0;
  const sectionGaps = coverage?.section_gaps || [];
  const hasCoverageSignal = sectionsSeen >= 3;
  const gapCount = Math.max(0, sectionsSeen - sectionsWithReqs);
  const coveragePct = sectionsSeen > 0 ? Math.round((sectionsWithReqs / sectionsSeen) * 100) : 0;
  const checkBusy = completenessStatus === 'processing';
  const lastCheckAt = coverage?.completeness_checked_at || data?.completeness_last_run_at;

  return (
    <div className="space-y-3" data-testid="requirements-list">
      {/* Coverage / truncation banner */}
      {coverageIncomplete && (
        <Card className="p-3 border-amber-200 bg-amber-50/60" data-testid="coverage-banner">
          <div className="flex items-start gap-2.5">
            <FileWarning className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-800">Some documents weren’t fully read</div>
              <p className="text-xs text-amber-700 mt-0.5">
                {coverage.docs_ok}/{coverage.docs_total} document(s) and {coverage.chunks_ok}/{coverage.chunks_total} section(s) processed.
                Results may be incomplete — try Refresh again, or upload a smaller / searchable PDF.
              </p>
              {(coverage.notes || []).slice(0, 3).map((n, i) => (
                <p key={i} className="text-[11px] text-amber-600 mt-0.5">• {n}</p>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Completeness / clause-coverage panel */}
      <Card className={`p-4 ${gapCount > 0 && hasCoverageSignal ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-[#FAFBFC]'}`} data-testid="completeness-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2.5 min-w-0">
            {gapCount > 0 && hasCoverageSignal
              ? <ScanSearch className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              : <ShieldCheck className="h-5 w-5 text-money-600 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800">Extraction completeness</div>
              {hasCoverageSignal ? (
                <p className="text-xs text-slate-600 mt-0.5" data-testid="coverage-summary">
                  <span className="font-semibold text-slate-800">{sectionsWithReqs}</span> of{' '}
                  <span className="font-semibold text-slate-800">{sectionsSeen}</span> detected clauses/sections have at least one requirement
                  {' '}(<span className={gapCount > 0 ? 'text-amber-700 font-medium' : 'text-money-600 font-medium'}>{coveragePct}%</span>).
                  {gapCount > 0
                    ? <> <span className="font-semibold text-amber-700">{gapCount}</span> section{gapCount !== 1 ? 's' : ''} have none — worth a quick check.</>
                    : ' Every detected section is covered.'}
                </p>
              ) : (
                <p className="text-xs text-slate-600 mt-0.5">
                  Run an AI second pass to catch any obligations the first extraction may have missed. Recovered items are grounded in a verbatim quote and added automatically.
                </p>
              )}
              {lastCheckAt && (
                <p className="text-[11px] text-slate-400 mt-0.5" data-testid="completeness-last-run">
                  Last completeness check {new Date(lastCheckAt).toLocaleString()}
                  {typeof coverage?.completeness_added === 'number' ? ` · recovered ${coverage.completeness_added}` : ''}
                </p>
              )}
            </div>
          </div>
          <Button size="sm" className="h-8 bg-brand hover:bg-brand-700 shrink-0" onClick={runCompletenessCheck}
            disabled={checking || checkBusy || refreshing} data-testid="completeness-check-btn">
            {(checking || checkBusy) ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5 mr-1.5" />}
            {checkBusy ? 'Checking…' : 'Run completeness check'}
          </Button>
        </div>

        {/* Gap list (sections with no extracted requirement) */}
        {hasCoverageSignal && gapCount > 0 && sectionGaps.length > 0 && (
          <div className="mt-2.5 pt-2.5 border-t border-amber-200/70">
            <button onClick={() => setGapsOpen((s) => !s)}
              className="flex items-center gap-1 text-[12px] font-medium text-amber-700 hover:text-amber-800"
              data-testid="coverage-gaps-toggle">
              {gapsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Sections with no requirement ({sectionGaps.length}{sectionGaps.length < gapCount ? '+' : ''})
            </button>
            {gapsOpen && (
              <div className="mt-1.5 grid gap-1 sm:grid-cols-2" data-testid="coverage-gaps-list">
                {sectionGaps.map((s, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600 bg-white/70 border border-amber-100 rounded px-2 py-1" data-testid={`coverage-gap-${i}`}>
                    <FileWarning className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      <span className="font-medium text-slate-700">{s.section}</span>
                      {s.title ? ` — ${s.title}` : ''}
                      {(s.document || s.page) && (
                        <span className="text-slate-400"> · {s.document || ''}{typeof s.page === 'number' ? ` p.${s.page}` : ''}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Proposal review banner */}
      {proposalStatus === 'processing' && (
        <Card className="p-3 flex items-center gap-2 border-brand-200 bg-brand-50/50" data-testid="proposal-processing">
          <Loader2 className="h-4 w-4 animate-spin text-brand" />
          <span className="text-sm text-slate-700">Checking your documents for changes to the baseline…</span>
        </Card>
      )}
      {proposalStatus === 'pending' && proposal && (
        <Card className="p-4 border-brand-200 bg-brand-50/50" data-testid="proposal-banner">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2.5">
              <GitCompare className="h-5 w-5 text-brand mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-slate-800">Review requirements no longer detected</div>
                <p className="text-xs text-slate-600 mt-0.5">
                  Refresh reconciled your baseline automatically
                  {(proposal?.matched || proposal?.auto_added || proposal?.auto_merged) ? (
                    <> (<span className="text-slate-700">{proposal.matched || 0} matched, {proposal.auto_added || 0} added, {proposal.auto_merged || 0} merged</span>)</>
                  ) : null}.
                  {' '}<span className="font-semibold text-danger-600">{removals.length}</span> requirement{removals.length !== 1 ? 's are' : ' is'} no longer found — nothing is deleted until you confirm.
                </p>
              </div>
            </div>
            <Button size="sm" className="bg-brand hover:bg-brand-700" onClick={() => setProposalOpen(true)} data-testid="proposal-review-btn">
              Review changes
            </Button>
          </div>
        </Card>
      )}

      {/* Summary + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-800">{reqs.length}</span> requirements from{' '}
          <span className="font-semibold text-slate-800">{docCount}</span> document{docCount !== 1 ? 's' : ''}
          {lowCount > 0 && <> · <span className="text-amber-600 font-medium" data-testid="low-conf-count">{lowCount} low-confidence</span></>}
          {unverifiedCount > 0 && <> · <button onClick={() => setUnverifiedOnly(true)} className="text-danger-600 font-medium hover:underline" data-testid="unverified-count">{unverifiedCount} unverified</button></>}
          {filtering && <> · <span className="text-brand font-medium">{filtered.length} match filters</span></>}
          {data?.extracted_at && (
            <span className="block text-[11px] text-slate-400 mt-0.5" data-testid="req-last-extracted">
              Last extracted {new Date(data.extracted_at).toLocaleString()}{version ? ` · v${version}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8" disabled={!reqs.length} data-testid="req-export-btn">
                <Download className="h-3.5 w-3.5 mr-1.5" />Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportCSV} data-testid="req-export-csv">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-money-600" />Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPDF} data-testid="req-export-pdf">
                <FileText className="h-3.5 w-3.5 mr-2 text-danger-600" />Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setHistoryOpen(true)} data-testid="req-history-btn">
            <History className="h-3.5 w-3.5 mr-1.5" />History
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={refresh} disabled={refreshing || proposalStatus === 'processing'} data-testid="requirements-refresh">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 space-y-2.5" data-testid="requirements-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search requirements…"
              className="h-8 pl-8 text-sm" data-testid="req-search" />
          </div>
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="req-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeOptions.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fConf} onValueChange={setFConf}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="req-filter-confidence"><SelectValue placeholder="Confidence" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All confidence</SelectItem>
              <SelectItem value="low" className="text-xs">Low (&lt;60%)</SelectItem>
              <SelectItem value="med" className="text-xs">Medium (60–80%)</SelectItem>
              <SelectItem value="high" className="text-xs">High (≥80%)</SelectItem>
              <SelectItem value="unscored" className="text-xs">Unscored</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fDoc} onValueChange={setFDoc}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="req-filter-doc"><SelectValue placeholder="Document" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All documents</SelectItem>
              {docOptions.map((d) => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}
            </SelectContent>
          </Select>
          {parentOptions.length > 0 && (
            <Select value={fParent} onValueChange={setFParent}>
              <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="req-filter-clause"><SelectValue placeholder="Clause / BOQ item" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clauses / items</SelectItem>
                {parentOptions.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant={sortWeak ? 'default' : 'outline'}
            className={`h-8 text-xs ${sortWeak ? 'bg-amber-500 hover:bg-amber-600' : ''} ${allFullConfidence ? 'hidden' : ''}`}
            onClick={() => setSortWeak((s) => !s)} data-testid="req-sort-weak">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />Weakest first
          </Button>
          <Button size="sm" variant={unverifiedOnly ? 'default' : 'outline'}
            className={`h-8 text-xs ${unverifiedOnly ? 'bg-danger-600 hover:bg-danger-700' : ''}`}
            onClick={() => setUnverifiedOnly((s) => !s)} data-testid="req-filter-unverified">
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Unverified only
          </Button>
        </div>
        <div className="flex items-center justify-end">
          {(filtering || sortWeak) && (
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="req-clear-filters">
              <X className="h-3.5 w-3.5" />Clear filters &amp; sort
            </button>
          )}
        </div>
      </Card>

      {types.length === 0 && (
        <Card className="p-8"><div className="text-center text-sm text-slate-500 flex flex-col items-center gap-2"><Filter className="h-5 w-5 text-slate-300" />No requirements match these filters.</div></Card>
      )}

      {/* Hierarchical, collapsible groups — grouped by unified TYPE */}
      {types.map((t) => {
        const g = grouped[t];
        const parentKeys = g.parentKeys || Object.keys(g.parents).sort();
        const open = typeOpen(t);
        const tm = typeMeta(t);
        return (
          <Card key={t} className="p-0 overflow-hidden" data-testid={`req-type-${t}`}>
            <button onClick={() => toggleType(t)}
              className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFBFC] hover:bg-slate-100/70 transition-colors border-b border-slate-200"
              data-testid={`req-type-toggle-${t}`}>
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                <span className={`inline-block h-2 w-2 rounded-full ${tm.dot}`} />
                {t}
              </span>
              <Badge variant="outline" className="text-[10px] bg-white text-slate-500 border-slate-200">{g.total}</Badge>
            </button>

            {open && (
              <div className="divide-y divide-slate-100">
                {/* Parent-grouped (clause / BOQ item) */}
                {parentKeys.map((pk) => {
                  const key = `${t}|${pk}`;
                  const items = g.parents[pk];
                  const pOpen = parentOpen(key);
                  return (
                    <div key={pk} data-testid={`req-parent-${pk}`}>
                      <button onClick={() => toggleParent(key)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`req-parent-toggle-${pk}`}>
                        <span className="flex items-center gap-2 text-[13px] font-medium text-slate-700 min-w-0">
                          {pOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                          <span className="truncate">{pk}</span>
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">{items.length}</span>
                      </button>
                      {pOpen && (
                        <div className="bg-slate-50/40">
                          {items.map((r) => <ReqRow key={r.id} r={r} indent onView={setView} onConfirm={confirmReq} onDismiss={dismissReq} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Loose requirements (no parent clause) */}
                {g.loose.map((r) => <ReqRow key={r.id} r={r} onView={setView} onConfirm={confirmReq} onDismiss={dismissReq} />)}
              </div>
            )}
          </Card>
        );
      })}

      {/* Proposal review dialog */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="proposal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitCompare className="h-5 w-5 text-brand" />Review proposed baseline changes</DialogTitle>
            <DialogDescription>
              Choose which changes to apply. Nothing changes until you click Apply.
              {proposal?.unchanged_count != null && <> {proposal.unchanged_count} requirement{proposal.unchanged_count !== 1 ? 's' : ''} unchanged.</>}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto -mx-1 px-1 space-y-4">
            {/* Additions */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-brand-700">
                  <Plus className="h-4 w-4" />New requirements ({additions.length})
                </div>
                {additions.length > 0 && (
                  <button className="text-[11px] text-slate-500 hover:text-slate-800" data-testid="proposal-toggle-adds"
                    onClick={() => setAddSel((s) => s.size === additions.length ? new Set() : new Set(additions.map((a) => a.id)))}>
                    {addSel.size === additions.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              {additions.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No new requirements detected.</p>
              ) : (
                <div className="space-y-1.5">
                  {additions.map((a) => (
                    <label key={a.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-brand-100 bg-brand-50/40 cursor-pointer hover:bg-brand-50" data-testid={`proposal-add-${a.id}`}>
                      <Checkbox checked={addSel.has(a.id)} onCheckedChange={() => setAddSel((s) => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })}
                        className="mt-0.5" data-testid={`proposal-add-check-${a.id}`} />
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 leading-snug">{a.requirement}</p>
                        <div className="flex items-center flex-wrap gap-x-2 mt-0.5 text-[11px] text-slate-400">
                          <Badge variant="outline" className="text-[9px] bg-white text-slate-500 border-slate-200">{a.type}</Badge>
                          {a.parent && <span className="truncate">{a.parent}</span>}
                          {a.document && <span>· {a.document}</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Removals */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-danger-700">
                  <Minus className="h-4 w-4" />No longer detected ({removals.length})
                </div>
                {removals.length > 0 && (
                  <button className="text-[11px] text-slate-500 hover:text-slate-800" data-testid="proposal-toggle-rems"
                    onClick={() => setRemSel((s) => s.size === removals.length ? new Set() : new Set(removals.map((r) => r.id)))}>
                    {remSel.size === removals.length ? 'Keep all' : 'Remove all'}
                  </button>
                )}
              </div>
              {removals.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nothing removed — all current requirements were re-detected.</p>
              ) : (
                <>
                  <p className="text-[11px] text-slate-400 mb-1.5">Tick an item to remove it from the baseline. Left unticked, it is kept.</p>
                  <div className="space-y-1.5">
                    {removals.map((r) => (
                      <label key={r.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-danger-100 bg-danger-50/30 cursor-pointer hover:bg-danger-50/60" data-testid={`proposal-rem-${r.id}`}>
                        <Checkbox checked={remSel.has(r.id)} onCheckedChange={() => setRemSel((s) => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
                          className="mt-0.5" data-testid={`proposal-rem-check-${r.id}`} />
                        <div className="min-w-0">
                          <p className="text-sm text-slate-700 leading-snug line-through decoration-slate-300">{r.requirement}</p>
                          <div className="flex items-center flex-wrap gap-x-2 mt-0.5 text-[11px] text-slate-400">
                            <Badge variant="outline" className="text-[9px] bg-white text-slate-500 border-slate-200">{r.type}</Badge>
                            {r.parent && <span className="truncate">{r.parent}</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Suggested merges (AI-confirmed near-duplicates) */}
            {merges.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-violet-700">
                    <Layers className="h-4 w-4" />Possible duplicates to merge ({merges.length})
                  </div>
                  <button className="text-[11px] text-slate-500 hover:text-slate-800" data-testid="proposal-toggle-merges"
                    onClick={() => setMergeSel((s) => s.size === merges.length ? new Set() : new Set(merges.map((m) => m.id)))}>
                    {mergeSel.size === merges.length ? 'Keep separate' : 'Merge all'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mb-1.5">These read as the same requirement (different wording). Ticked = merge into one, keeping every source.</p>
                <div className="space-y-1.5">
                  {merges.map((m) => (
                    <label key={m.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-violet-100 bg-violet-50/40 cursor-pointer hover:bg-violet-50" data-testid={`proposal-merge-${m.id}`}>
                      <Checkbox checked={mergeSel.has(m.id)} onCheckedChange={() => setMergeSel((s) => { const n = new Set(s); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })}
                        className="mt-0.5" data-testid={`proposal-merge-check-${m.id}`} />
                      <div className="min-w-0 space-y-1">
                        {(m.members || []).map((mem, i) => (
                          <p key={i} className="text-sm text-slate-700 leading-snug flex items-start gap-1.5">
                            <span className={`shrink-0 text-[10px] mt-0.5 ${mem.id === m.canonical_id ? 'text-violet-700 font-semibold' : 'text-slate-400'}`}>
                              {mem.id === m.canonical_id ? 'KEEP' : 'dup'}
                            </span>
                            <span className={mem.id === m.canonical_id ? '' : 'text-slate-500'}>{mem.requirement}{mem.document ? ` · ${mem.document}` : ''}</span>
                          </p>
                        ))}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={discardProposal} disabled={applying} data-testid="proposal-discard-btn">
              Discard all
            </Button>
            <Button className="bg-brand hover:bg-brand-700" onClick={applyProposal} disabled={applying} data-testid="proposal-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              Apply {(addSel.size + remSel.size + mergeSel.size) > 0 ? `(${addSel.size} add, ${remSel.size} remove, ${mergeSel.size} merge)` : 'changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refresh history + rollback dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-brand" />Requirements history</DialogTitle>
            <DialogDescription>
              Every extraction and refresh is versioned. Restore a recent version if a change was made in error.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto -mx-1 px-1 space-y-2">
            {audit.length === 0 && <p className="text-sm text-slate-400 italic">No history yet.</p>}
            {(data?.removed_log || []).length > 0 && (
              <div className="mb-2" data-testid="history-removed">
                <div className="text-[11px] font-semibold text-danger-700 uppercase tracking-wide mb-1">Removed requirements</div>
                {(data.removed_log || []).slice(0, 20).map((x, i) => (
                  <div key={i} className="text-[11px] text-slate-500 flex items-start gap-1.5 py-0.5" data-testid={`history-removed-${i}`}>
                    <Minus className="h-3 w-3 mt-0.5 text-danger-500 shrink-0" />
                    <span><span className="line-through decoration-slate-300">{(x.requirement || '').slice(0, 70)}</span>
                      {' '}· removed v{x.removed_version} by {x.by || 'Unknown'}{x.added_version ? ` · added v${x.added_version}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
            {audit.map((a, i) => {
              const canRestore = (data?.restorable_versions || []).some((v) => v.version === a.version);
              const actionLabel = {
                initial_extract: 'Extracted', proposal_applied: 'Refresh applied',
                proposal_discarded: 'Refresh discarded', rolled_back: 'Rolled back',
                completeness_check: 'Completeness check',
              }[a.action] || a.action;
              return (
                <div key={i} className="p-2.5 rounded-md border border-slate-200 bg-white" data-testid={`history-entry-${a.version}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">v{a.version}</Badge>
                      <span className="text-sm font-medium text-slate-800">{actionLabel}</span>
                    </div>
                    {canRestore && a.action !== 'rolled_back' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => rollback(a.version)} data-testid={`history-restore-${a.version}`}>
                        <RefreshCw className="h-3 w-3 mr-1" />Restore
                      </Button>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {a.by || 'System'} · {a.at ? new Date(a.at).toLocaleString() : ''}
                    {(a.added > 0 || a.removed > 0) && (
                      <> · <span className="text-brand-600">+{a.added}</span> <span className="text-danger-600">−{a.removed}</span></>
                    )}
                    {a.restored_from_version != null && <> · from v{a.restored_from_version}</>}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <DocumentViewerDialog fileId={view?.file_id} filename={view?.filename} highlight={view?.highlight || []}
        open={!!view} onOpenChange={(o) => { if (!o) setView(null); }} />
    </div>
  );
};

const SourceLink = ({ src, onView, testId }) => {
  const label = `${src.document || 'Document'}${src.section ? ` · ${src.section}` : ''}${src.page ? ` · p.${src.page}` : ''}`;
  if (src.doc_type === 'communication') {
    return (
      <span className="flex items-center gap-1 text-slate-500" data-testid={testId}>
        <MessageSquareText className="h-3 w-3" />From communications · {src.document}
      </span>
    );
  }
  if (src.file_id) {
    return (
      <button onClick={() => onView({ file_id: src.file_id, filename: src.document, highlight: [src.source_quote, src.section, src.parent] })}
        className="flex items-center gap-1 text-brand hover:underline" data-testid={testId}>
        <FileText className="h-3 w-3" />{label}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1 text-slate-500" data-testid={testId}>
      <FileText className="h-3 w-3" />{label}
    </span>
  );
};

const ReqRow = ({ r, indent, onView, onConfirm, onDismiss }) => {
  const [showSources, setShowSources] = useState(false);
  const pending = r.discovered && r.review_status === 'pending';
  const sources = (r.sources && r.sources.length) ? r.sources
    : [{ document: r.document, doc_type: r.doc_type, file_id: r.file_id, page: r.page, section: r.section, parent: r.parent, source_quote: r.source_quote }];
  const multi = sources.length > 1;
  const confPct = (typeof r.confidence === 'number') ? Math.round(r.confidence * 100) : null;
  const isLow = typeof r.confidence === 'number' && r.confidence < LOW_CONF;
  const isUnverified = r.verified === false;
  const reasonText = r.verification_reason === 'no_source_quote'
    ? 'no supporting quote'
    : (r.verification_reason === 'quote_not_found_in_source' ? 'quote not found in source' : '');

  return (
    <div className={`px-4 py-3 ${indent ? 'pl-9' : ''} ${pending ? 'bg-amber-50/40' : ''} ${isUnverified ? 'border-l-2 border-danger-300' : ''}`} data-testid={`req-item-${r.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 leading-relaxed" data-testid={`req-text-${r.id}`}>{r.requirement}</p>

          {/* Verbatim quote from the source document */}
          {r.source_quote && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-slate-500 italic border-l-2 border-slate-200 pl-2" data-testid={`req-quote-${r.id}`}>
              <Quote className="h-3 w-3 mt-0.5 shrink-0 text-slate-300" />
              <span className="line-clamp-2">“{r.source_quote}”</span>
            </div>
          )}

          {/* Meta line: flags + page + confidence + verification */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1.5 text-[11px] text-slate-400">
            {pending && (
              <Badge variant="outline" className="text-[9px] bg-amber-100 text-amber-800 border-amber-300" data-testid={`req-review-${r.id}`}>
                Discovered — needs review
              </Badge>
            )}
            {isUnverified && (
              <Badge variant="outline" className="text-[9px] bg-danger-50 text-danger-700 border-danger-300 flex items-center gap-1" data-testid={`req-unverified-${r.id}`}>
                <ShieldAlert className="h-2.5 w-2.5" />Unverified — review{reasonText ? ` · ${reasonText}` : ''}
              </Badge>
            )}
            {typeof r.page === 'number' && (
              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 flex items-center gap-1" data-testid={`req-page-${r.id}`}>
                <MapPin className="h-2.5 w-2.5" />Page {r.page}
              </Badge>
            )}
            {typeof r.added_version === 'number' && r.added_version > 1 && (
              <span className="text-slate-400" data-testid={`req-added-${r.id}`}>Added v{r.added_version}</span>
            )}
            {r.recovered && (
              <Badge variant="outline" className="text-[9px] bg-brand-50 text-brand-700 border-brand-200 flex items-center gap-1" data-testid={`req-recovered-${r.id}`}>
                <ScanSearch className="h-2.5 w-2.5" />Recovered
              </Badge>
            )}
            {r.authority && r.authority !== 'Binding' && (
              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200" data-testid={`req-authority-${r.id}`}>
                {r.authority}
              </Badge>
            )}
            {r.mandatory === false && <span className="text-slate-400 italic">Optional</span>}
            {isLow ? (
              <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300 flex items-center gap-1" data-testid={`req-lowconf-${r.id}`}>
                <AlertTriangle className="h-2.5 w-2.5" />Low confidence · {confPct}%
              </Badge>
            ) : confPct !== null && (
              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200" data-testid={`req-confidence-${r.id}`}>
                {confPct}% confidence
              </Badge>
            )}
          </div>

          {/* Sources — link every document/page this requirement was found in */}
          <div className="mt-1.5 text-[11px]">
            {multi ? (
              <div data-testid={`req-sources-${r.id}`}>
                <button onClick={() => setShowSources((s) => !s)}
                  className="flex items-center gap-1 text-slate-500 hover:text-slate-800"
                  data-testid={`req-sources-toggle-${r.id}`}>
                  <Layers className="h-3 w-3" />
                  Found in {sources.length} documents
                  {showSources ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                {showSources && (
                  <div className="mt-1 space-y-1 pl-1 border-l border-slate-200">
                    {sources.map((s, i) => (
                      <div key={i} className="pl-2"><SourceLink src={s} onView={onView} testId={`req-source-${r.id}-${i}`} /></div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <SourceLink src={sources[0]} onView={onView} testId={`req-source-${r.id}`} />
            )}
          </div>

          {pending && (
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" className="h-7 text-xs bg-money hover:bg-money-700" onClick={() => onConfirm(r.id)} data-testid={`req-confirm-${r.id}`}>
                <Check className="h-3.5 w-3.5 mr-1" />Confirm
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onDismiss(r.id)} data-testid={`req-dismiss-${r.id}`}>
                <X className="h-3.5 w-3.5 mr-1" />Dismiss
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
